import { Box, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import dayjs from "dayjs";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { getEmbedAccessToken } from "@/api/embedAuth";
import {
  SprengnetterValuationHubValuationReportModelResponseAvmPriceResponse as AvmPriceResponse,
  SprengnetterValuationHubProsaWebModelResponseProsaWebResponse as ProsaWebResponse,
  ValuationHubCoreModelDtoValuationReportConfigurationDto,
  ValuationHubCoreModelRealEstateAddress,
} from "@/api/generatedApi";
import { ReportApi, ReportFormat, ReportTracking } from "@/components/reportApi";
import { PropertyInformation } from "@/types/refineRealEstate";

import { useValuationReportConfigFormData } from "./ValuationReportConfigFormContext";
import {
  ContactInformationSection,
  LogoAndColorsSection,
  PersonalizationSection,
  PriceAndValuationMethodsSection,
  TitleSection,
} from "./sections";

const cloneReportConfiguration = <T,>(value: T): T => structuredClone(value);

const getAccessToken = async (): Promise<string | null> => {
  const embedAccessToken = getEmbedAccessToken();

  if (embedAccessToken) {
    return embedAccessToken;
  }

  const { oktaAuth } = await import("@/oktaAuth");
  return oktaAuth.getAccessToken() ?? null;
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createStyles = (theme: Theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.analysisLayout.panelGap,
  },
});

interface ValuationReportConfigurationProps {
  address: ValuationHubCoreModelRealEstateAddress | null;
  propertyInformation: PropertyInformation | null;
  reportAskingPrice?: number | null;
  onAskingPriceChange?: (price: number | null) => void;
  isAskingPriceEditable?: boolean;
  beforeCreateReport?: () => Promise<void>;
  avmPrice: AvmPriceResponse | null;
  prosaWebPrice: ProsaWebResponse | null;
  setIsReportConfigurationOpen: (isOpen: boolean) => void;
  setIsReportCreatedModalOpen: (isOpen: boolean) => void;
  setCreatedReportUrl: (url: string | null) => void;
  setCreatedReportFileName: (fileName: string | null) => void;
  refetchStatus: () => unknown | Promise<unknown>;
  reportApi: ReportApi;
  tracking?: ReportTracking;
  formId?: string;
  onGenerateReportLoadingChange?: (isLoading: boolean) => void;
  sendImageUrls?: boolean;
  reportFormat?: ReportFormat;
  getReportFormat?: () => ReportFormat;
  onReportGenerated?: (format: ReportFormat) => void;
}

export const ValuationReportConfiguration = ({
  address,
  propertyInformation,
  reportAskingPrice,
  onAskingPriceChange,
  isAskingPriceEditable = false,
  beforeCreateReport,
  avmPrice,
  prosaWebPrice,
  setIsReportConfigurationOpen,
  setIsReportCreatedModalOpen,
  refetchStatus,
  reportApi,
  tracking,
  setCreatedReportUrl,
  setCreatedReportFileName,
  formId = "valuation-report-configuration-form",
  onGenerateReportLoadingChange,
  sendImageUrls = false,
  reportFormat = "pdf",
  getReportFormat,
  onReportGenerated,
}: ValuationReportConfigurationProps): JSX.Element => {
  const theme = useTheme();
  const styles = createStyles(theme);
  const { formData, setFormData } = useValuationReportConfigFormData();
  const [pendingImageUploadsCount, setPendingImageUploadsCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const methods = useForm({
    defaultValues: cloneReportConfiguration(formData),
  });
  const isSubmitting = reportApi.isLoading || reportApi.isFetching;
  const isBusy = isSubmitting || pendingImageUploadsCount > 0;

  useEffect(() => {
    if (JSON.stringify(methods.getValues()) !== JSON.stringify(formData)) {
      methods.reset(cloneReportConfiguration(formData));
    }
  }, [formData, methods]);

  useEffect(() => {
    const subscription = methods.watch((value) => {
      setFormData((prev) => {
        const nextValue = cloneReportConfiguration(value as typeof prev);

        if (JSON.stringify(prev) === JSON.stringify(nextValue)) {
          return prev;
        }

        return nextValue;
      });
    });

    return (): void => subscription.unsubscribe();
  }, [methods, setFormData]);

  const handleImagePendingChange = (isPending: boolean): void => {
    setPendingImageUploadsCount((prev) => {
      if (isPending) {
        return prev + 1;
      }

      return Math.max(0, prev - 1);
    });
  };

  const handleCreateReport = async (): Promise<void> => {
    if (isBusy) {
      onGenerateReportLoadingChange?.(false);
      return;
    }

    onGenerateReportLoadingChange?.(true);
    try {
      formRef.current?.querySelector<HTMLInputElement>("[data-report-asking-price-input]")?.blur();
      await beforeCreateReport?.();
    } catch (ex) {
      console.error("Failed to save the asking price before creating the report", ex);
      onGenerateReportLoadingChange?.(false);
      return;
    }

    const selectedReportFormat = getReportFormat?.() ?? reportFormat;
    const payload = methods.getValues();
    const blobToDataUrl = (blob: Blob): Promise<string> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = (): void => {
          if (typeof reader.result === "string") {
            resolve(reader.result);
            return;
          }
          reject(new Error("Failed to read blob as data URL"));
        };
        reader.onerror = (): void => reject(reader.error ?? new Error("Failed to read image blob"));
        reader.readAsDataURL(blob);
      });

    const toReportImagePayload = async (value?: string | null, fieldName?: string): Promise<string | undefined> => {
      if (!value) return undefined;
      if (value.startsWith("data:image/")) return value;

      const getMimeTypeFromUrl = (urlValue: string): string | undefined => {
        const lowerValue = urlValue.toLowerCase();
        if (lowerValue.includes(".png")) return "image/png";
        if (lowerValue.includes(".jpg") || lowerValue.includes(".jpeg")) return "image/jpeg";
        if (lowerValue.includes(".webp")) return "image/webp";
        if (lowerValue.includes(".gif")) return "image/gif";
        return undefined;
      };

      const accessToken = await getAccessToken();
      let response = await fetch(value, {
        credentials: "omit",
      });
      if (!response.ok && accessToken) {
        response = await fetch(value, {
          credentials: "omit",
          headers: { Authorization: `Bearer ${accessToken}` },
        });
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch ${fieldName ?? "image"} from URL (${response.status})`);
      }
      const contentType = response.headers.get("content-type") ?? "";
      const normalizedContentType = contentType.toLowerCase();
      const inferredMimeType =
        (normalizedContentType.startsWith("image/") ? normalizedContentType.split(";")[0] : undefined) ??
        getMimeTypeFromUrl(value);
      const blob = await response.blob();
      const dataUrl = await blobToDataUrl(blob);
      if (dataUrl.startsWith("data:image/")) {
        return dataUrl;
      }

      if (inferredMimeType && dataUrl.startsWith("data:application/octet-stream;")) {
        return dataUrl.replace("data:application/octet-stream;", `data:${inferredMimeType};`);
      }

      throw new Error(`Failed to convert ${fieldName ?? "image"} to base64 image (${contentType || "unknown"})`);
    };

    if (!Array.isArray(payload.real_estate_images)) {
      payload.real_estate_images = [];
    }
    const safeToReportImagePayload = async (value?: string | null, fieldName?: string): Promise<string | undefined> => {
      try {
        return await toReportImagePayload(value, fieldName);
      } catch (error) {
        console.error(
          "Failed to convert " +
            (fieldName || "report image") +
            " to base64. Report generation will continue without this image.",
          error,
        );
        return undefined;
      }
    };

    type ReportPayloadWithImageUrls = ValuationHubCoreModelDtoValuationReportConfigurationDto & {
      company_logo_url?: string;
      profile_picture_url?: string;
    };
    const reportPayload = payload as ReportPayloadWithImageUrls;
    const isDataUrl = (value?: string | null): boolean => Boolean(value?.startsWith("data:image/"));

    if (sendImageUrls) {
      if (payload.company_logo && !isDataUrl(payload.company_logo)) {
        reportPayload.company_logo_url = payload.company_logo;
        payload.company_logo = undefined;
      }

      if (payload.profile_picture && !isDataUrl(payload.profile_picture)) {
        reportPayload.profile_picture_url = payload.profile_picture;
        payload.profile_picture = undefined;
      }
    } else {
      const [companyLogo, profilePicture] = await Promise.all([
        safeToReportImagePayload(payload.company_logo, "company_logo"),
        safeToReportImagePayload(payload.profile_picture, "profile_picture"),
      ]);

      payload.company_logo = companyLogo;
      payload.profile_picture = profilePicture;
    }

    if (!payload.reference_number) {
      const currentDate = dayjs().format("YYYY-MM-DD");
      const referenceNumber = `Bewertung_${address?.street}_${address?.house_number}_${address?.town}_${currentDate}`;
      payload.reference_number = referenceNumber;
    }
    try {
      const result = await reportApi.createReport(payload, selectedReportFormat);
      tracking?.onCreate?.();
      onReportGenerated?.(selectedReportFormat); // Track successful report creation
      const url = window.URL.createObjectURL(result);
      const reportFileName = `${payload.reference_number}.${selectedReportFormat}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = reportFileName;
      a.click();
      setCreatedReportUrl(url);
      setCreatedReportFileName(reportFileName);
      tracking?.onDownload?.(); // Track successful report download
      setIsReportConfigurationOpen(false);
      setIsReportCreatedModalOpen(true);
      refetchStatus();
    } catch (ex) {
      console.error("Failed to create a report", ex);
    } finally {
      onGenerateReportLoadingChange?.(false);
    }
  };

  return (
    <>
      <FormProvider {...methods}>
        <Box
          ref={formRef}
          id={formId}
          sx={styles.mainContainer}
          component="form"
          onSubmit={(event) => {
            event.preventDefault();
            void handleCreateReport();
          }}
        >
          <TitleSection address={address} />
          <Divider />
          <PriceAndValuationMethodsSection
            propertyInformation={propertyInformation}
            reportAskingPrice={reportAskingPrice}
            onAskingPriceChange={onAskingPriceChange}
            isAskingPriceEditable={isAskingPriceEditable}
            avmPriceResult={avmPrice}
            prosaWebPriceResult={prosaWebPrice}
          />
          <Divider />
          <PersonalizationSection />
          <LogoAndColorsSection disabled={isBusy} onImagePendingChange={handleImagePendingChange} />
          <ContactInformationSection />
        </Box>
      </FormProvider>
    </>
  );
};
